import React, { useEffect, useState } from 'react';
import { TYPE, useTheme } from '../theme';
import { Badge, Card, ErrorState, Loader, Switch } from '../components/ui';
import { fetchIntegrationVisibility, setIntegrationHidden, type IntegrationKey, type IntegrationVisibility } from '../lib/integrationVisibility';

// IN3 · Admin → Integrationer: dölj integrationer under Inställningar, i markens integrationsflik och i
// gruppchatten. Servern kontrollerar admin och spärrar nya kopplingar för dolda; befintliga kopplingar rörs inte.

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : null);

export function IntegrationVisibilityAdmin() {
  const T = useTheme();
  const [v, setV] = useState<IntegrationVisibility | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<IntegrationKey | null>(null);

  useEffect(() => {
    fetchIntegrationVisibility(true).then(d => (d ? setV(d) : setError('Kunde inte hämta integrationerna.')));
  }, []);

  async function toggle(key: IntegrationKey, visible: boolean) {
    setBusy(key);
    try { setV(await setIntegrationHidden(key, !visible)); }
    catch (e: any) { alert(e.message || String(e)); }
    finally { setBusy(null); }
  }

  if (error) return <ErrorState message={error} />;
  if (!v) return <Loader text="Hämtar integrationer…" />;

  return (
    <div>
      <p style={{ ...TYPE.small, color: T.textSub, margin: '0 0 14px' }}>
        En dold integration syns inte för användarna under Inställningar, i markens integrationsflik eller i gruppchatten, och nya kopplingar går inte att starta.
        Kopplingar som redan finns kopplas inte bort och fortsätter att fungera. Du som admin ser och kan koppla alla.
      </p>
      <Card padding={16}>
        <div style={{ display: 'grid', gap: 12 }}>
          {v.integrations.map(i => (
            <div key={i.key} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ color: T.text, fontWeight: 700, flex: '1 1 200px', minWidth: 0 }}>{i.label}</span>
              {i.hidden ? <Badge tone="info">Dold för användare</Badge> : <Badge tone="ok" dot>Synlig</Badge>}
              <Switch ariaLabel={`Visa ${i.label} för användare`} checked={!i.hidden} disabled={busy === i.key} onChange={on => void toggle(i.key, on)} />
            </div>
          ))}
        </div>
      </Card>
      {v.updatedBy && <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 8 }}>Senast ändrad av {v.updatedBy} {fmt(v.updatedAt)}</div>}
    </div>
  );
}
